import { Tag } from '@components';
import { Box, Button, Tooltip } from '@mui/material';
import { ArchiveBox, Refresh } from 'iconsax-react';
import { type FC, type PropsWithChildren, useMemo } from 'react';

import { DISPATCH_SYSTEM_NAMES } from '@core/constants/integrations';
import Enums from '@core/types/api/Enums';
import type { Integration } from '@core/types/api/Integration/Integration';
import type { NestedKeyOf } from '@core/utils/types';
import { DataTable } from '@web/components/DataTable/DataTable';
import type { ColumnConfig } from '@web/components/DataTable/TableDataModel';
import { useApiTableDataWithoutAggregations } from '@web/components/DataTable/useApiTableData';
import type { MutationRouteBarrelTypes } from '@web/utils/ApiClient';

const MaybeArchivedFormatter: FC<PropsWithChildren<{ row: Integration }>> = ({ row, children }) => (
  <Box sx={row.isArchived ? { opacity: 0.5, filter: 'grayscale(1)' } : undefined}>{children}</Box>
);

const createIntegrationTableColumnsColumnConfig = ({
  archiveIntegration,
  tenantIdOverride,
}: Pick<Props, 'archiveIntegration' | 'tenantIdOverride'>): ColumnConfig<
  Integration,
  NestedKeyOf<Integration>
>[] => {
  return [
    {
      id: 'name',
      label: 'Name',
      type: 'string',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <MaybeArchivedFormatter row={row}>{row.name}</MaybeArchivedFormatter>
      ),
    },
    {
      id: 'integrationType',
      label: 'Type',
      type: 'custom',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <MaybeArchivedFormatter row={row}>
          <Tag variant='Secondary' label={DISPATCH_SYSTEM_NAMES[row.integrationType]} />
        </MaybeArchivedFormatter>
      ),
    },
    {
      id: 'externalIdentifier',
      label: 'External Identifier',
      type: 'string',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <MaybeArchivedFormatter row={row}>
          <code style={{ fontSize: '0.875rem' }}>{row.externalIdentifier}</code>
        </MaybeArchivedFormatter>
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      type: 'custom',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <MaybeArchivedFormatter row={row}>
          <Tag
            variant={row.isActive ? 'Success' : 'Gray'}
            label={row.isActive ? 'Active' : 'Inactive'}
          />
        </MaybeArchivedFormatter>
      ),
    },
    {
      id: 'isArchived',
      label: '',
      type: 'custom',
      isDisplayed: true,
      formatValueForWeb: (_, integration) =>
        integration.isArchived ? (
          <Tooltip title='Unarchive'>
            <Button
              disableElevation
              disableRipple
              sx={{ minWidth: 'auto', position: 'unset' }}
              onClick={(): void => {
                archiveIntegration({
                  args: {
                    pathParams: { id: integration.id, tenantID: tenantIdOverride },
                    body: {},
                  },
                });
              }}
            >
              <Refresh variant='TwoTone' />
            </Button>
          </Tooltip>
        ) : (
          <Tooltip title='Archive'>
            <Button
              disableRipple
              sx={{ minWidth: 'auto', position: 'unset' }}
              onClick={(): void => {
                archiveIntegration({
                  args: {
                    pathParams: { id: integration.id, tenantID: tenantIdOverride },
                    body: {},
                  },
                });
              }}
            >
              <ArchiveBox variant='TwoTone' />
            </Button>
          </Tooltip>
        ),
    },
  ];
};

const isNotArchived = (integration: Integration): boolean => !integration.isArchived;

type Props = {
  editIntegration(integration: Integration): void;
  archiveIntegration(
    args: Pick<MutationRouteBarrelTypes['POST integration archive as admin'], 'args'>,
  ): void;
  tenantIdOverride: string;
};

export const IntegrationList: FC<Props> = ({
  editIntegration,
  archiveIntegration,
  tenantIdOverride,
}) => {
  const columnConfigs = useMemo(
    () => createIntegrationTableColumnsColumnConfig({ archiveIntegration, tenantIdOverride }),
    [archiveIntegration, tenantIdOverride],
  );

  const integrationTableModel = useApiTableDataWithoutAggregations({
    rowsRouteKey: 'GET integrations by tenant ID as admin',
    rowsPathParams: { tenantID: tenantIdOverride },
    initialSortBy: {
      direction: Enums.SortDirection.Ascending,
      name: 'name',
    },
    extractRows: (integrationsList) => integrationsList.items,
    makeColumnConfigs: () => columnConfigs,
  });

  return (
    <DataTable
      tableName='Integrations'
      onEditPress={editIntegration}
      canEditRow={isNotArchived}
      tableModel={integrationTableModel}
    />
  );
};
